import { addProtocolIfNeeded } from '@app/utils/addProtocolPrefixIfNeeded';

// Build timestamp injected at build time by Vite - changes on every rebuild
declare const __BUILD_TIMESTAMP__: number;

type LicenseTypeLabelField = 'title' | 'description';

interface AppConfig {
  apiBaseUrl: string;
  ssoLoginPath: string;
  ssoLogoutPath: string;
  ssoCallbackPath: string;
  /** Client-side inactivity timeout in milliseconds. Default: 8 hours. */
  idleTimeoutMs?: number;
  licenseTypeLabelField: LicenseTypeLabelField;
  /**
   * Optional override for inspection process type.
   * 1 = STANDARD, 2 = BACKOFFICE
   */
  inspectionProcessTypeOverride?: number;
  /**
   * When true, the license note field is required on create/edit when the "mobile app" switch is off.
   */
  licenseNoteRequiredWhenMobileAppFalse?: boolean;
  /**
   * When false, the fisher add/edit modal does not require country (zip/town can be saved without country).
   * Omit or set to true for current default behavior.
   */
  fisherCountryRequired?: boolean;
  /**
   * When true, the fishers table bulk edit mass action is hidden (ACL alone no longer shows it).
   */
  fisherBulkEditHidden?: boolean;
  /**
   * When true, the fisher detail page does not show the work assignments block (and skips loading them).
   */
  fisherWorkAssignmentsHidden?: boolean;
  /**
   * When false, the hunter add/edit modal does not require country.
   * Omit or set to true for current default behavior.
   */
  hunterCountryRequired?: boolean;
  /**
   * When true, the hunters table bulk edit mass action is hidden.
   */
  hunterBulkEditHidden?: boolean;
  /**
   * When true, the hunter detail page does not show the work assignments block (and skips loading them).
   */
  hunterWorkAssignmentsHidden?: boolean;
  /**
   * When true, the license detail view hides the fisher phone row under user information.
   * (Use this instead of fisher entrypoint overrides, which also drive dynamic fields and can duplicate phone.)
   */
  licenseViewFisherPhoneHidden?: boolean;
  /**
   * When true, the license detail view hides the language row (common.language) under license information.
   * @default false
   */
  licenseViewLanguageHidden?: boolean;
  /**
   * When true, the license detail view hides the language row (common.language) under license information.
   * @default false
   */
  licenseViewBillingReferenceHidden?: boolean;
  /**
   * When true, the licenses list filter "not printed" is hidden and never sent to the API.
   * @default false
   */
  licensesNotPrintedFilterHidden?: boolean;
  /**
   * When true, the licenses list filter "not formal verified" is hidden and never sent to the API.
   * @default false
   */
  licensesNotFormalVerifiedFilterHidden?: boolean;
  /**
   * When true, cancelled fishing licenses can still be printed (the PDF carries a cancelled watermark).
   * @default false
   */
  licensesCancelledPrintEnabled?: boolean;
  /**
   * When true, the hunting licenses list filter "not printed" is hidden and never sent to the API.
   * @default false
   */
  huntingLicensesNotPrintedFilterHidden?: boolean;
  /**
   * When true, the hunting licenses list filter "not formal verified" is hidden and never sent to the API.
   * @default false
   */
  huntingLicensesNotFormalVerifiedFilterHidden?: boolean;
  /**
   * When true, the new license modal shows the point-of-sale dropdown and sends salesPoint to getLicenseTypes.
   * @default false
   */
  enableSalesPoint?: boolean;
  /**
   * Display format for the SaNa number, shown as a placeholder on the SaNa number input in the
   * new fisher modal (e.g. "xxx-xxxx-xxxx/xxx-x"). When empty/undefined, no custom placeholder is used.
   */
  sanaFormat?: string;
  version?: string;
  /**
   * When false, newly created licenses are not automatically validated after creation.
   * @default true
   */
  autoValidateLicense?: boolean;
  /**
   * Master switch for error reporting. A deployment reports nothing unless this is
   * true AND `sentryDsn` is set. @default false
   */
  sentryEnabled?: boolean;
  /** Sentry DSN. When empty, error reporting is disabled. */
  sentryDsn?: string;
  /** Sentry environment label (e.g. "demo", "production"). Defaults to the Vite build mode. */
  sentryEnvironment?: string;
  /** Share (0–1) of sessions with an error that get a replay recording. @default 0.1 */
  sentryReplayOnErrorSampleRate?: number;
  /*
   * `sentryOrg` and `sentryProject` also live in config.json, but they are read by
   * `vite.config.ts` at build time for the source map upload. The app ignores them.
   */
}

const DEFAULT_SSO_LOGIN_PATH = '/sso/login/keycloak';
const DEFAULT_SSO_LOGOUT_PATH = '/sso/logout/keycloak';
const DEFAULT_SSO_CALLBACK_PATH = '';
const DEFAULT_LICENSE_TYPE_LABEL_FIELD: LicenseTypeLabelField = 'title';

type RawAppConfig = Omit<Partial<AppConfig>, 'inspectionProcessTypeOverride'> & {
  inspectionProcessTypeOverride?: unknown;
  /** @deprecated Use `licenseNoteRequiredWhenMobileAppFalse`. */
  licenseNoteRequiredWhenMobileApp?: boolean;
};

const CODE_DEFAULTS: RawAppConfig = {
  apiBaseUrl: '',
  ssoLoginPath: DEFAULT_SSO_LOGIN_PATH,
  ssoLogoutPath: DEFAULT_SSO_LOGOUT_PATH,
  ssoCallbackPath: DEFAULT_SSO_CALLBACK_PATH,
  licenseTypeLabelField: DEFAULT_LICENSE_TYPE_LABEL_FIELD,
  licenseNoteRequiredWhenMobileAppFalse: false,
  fisherCountryRequired: true,
  fisherBulkEditHidden: false,
  fisherWorkAssignmentsHidden: false,
  licenseViewFisherPhoneHidden: false,
  licenseViewLanguageHidden: false,
  licenseViewBillingReferenceHidden: false,
  licensesNotPrintedFilterHidden: false,
  licensesNotFormalVerifiedFilterHidden: false,
  licensesCancelledPrintEnabled: false,
  enableSalesPoint: false,
  autoValidateLicense: true,
};

let config: AppConfig | null = null;

const fetchJsonConfig = async (path: string, buildVersion: number): Promise<RawAppConfig> => {
  const response = await fetch(`${path}?v=${buildVersion}`, {
    cache: 'no-cache',
    headers: {
      'Cache-Control': 'no-cache',
    },
  });

  if (!response.ok) {
    return {};
  }

  return (await response.json()) as RawAppConfig;
};

const mergeRuntimeConfig = (defaultConfig: RawAppConfig, overrideConfig: RawAppConfig): RawAppConfig => ({
  ...defaultConfig,
  ...overrideConfig,
});

export const loadConfig = async () => {
  try {
    // Use cache: 'no-cache' to ensure we always get the latest config files.
    // Build timestamp in query parameter ensures each build gets fresh config.
    const buildVersion = typeof __BUILD_TIMESTAMP__ !== 'undefined' ? __BUILD_TIMESTAMP__ : Date.now();
    const [defaultConfig, overrideConfig] = await Promise.all([
      fetchJsonConfig('/config-default.json', buildVersion),
      fetchJsonConfig('/config.json', buildVersion),
    ]);

    const loadedConfig = mergeRuntimeConfig(mergeRuntimeConfig(CODE_DEFAULTS, defaultConfig), overrideConfig);

    const licenseTypeLabelField =
      loadedConfig.licenseTypeLabelField === 'description' ? 'description' : DEFAULT_LICENSE_TYPE_LABEL_FIELD;

    // Ensure apiBaseUrl has protocol prefix (https://) if missing
    const apiBaseUrl = addProtocolIfNeeded(loadedConfig.apiBaseUrl) ?? '';

    const inspectionProcessTypeOverrideRaw = loadedConfig.inspectionProcessTypeOverride;
    const inspectionProcessTypeOverride =
      inspectionProcessTypeOverrideRaw == null || inspectionProcessTypeOverrideRaw === ''
        ? undefined
        : Number(inspectionProcessTypeOverrideRaw);
    const normalizedInspectionProcessTypeOverride =
      Number.isFinite(inspectionProcessTypeOverride) &&
      (inspectionProcessTypeOverride === 1 || inspectionProcessTypeOverride === 2)
        ? inspectionProcessTypeOverride
        : undefined;

    const licenseNoteRequiredWhenMobileAppFalse =
      loadedConfig.licenseNoteRequiredWhenMobileAppFalse !== undefined
        ? loadedConfig.licenseNoteRequiredWhenMobileAppFalse === true
        : loadedConfig.licenseNoteRequiredWhenMobileApp === true;

    config = {
      apiBaseUrl,
      ssoLoginPath: loadedConfig.ssoLoginPath || DEFAULT_SSO_LOGIN_PATH,
      ssoLogoutPath: loadedConfig.ssoLogoutPath || DEFAULT_SSO_LOGOUT_PATH,
      ssoCallbackPath: loadedConfig.ssoCallbackPath ?? DEFAULT_SSO_CALLBACK_PATH,
      idleTimeoutMs:
        typeof loadedConfig.idleTimeoutMs === 'number' && loadedConfig.idleTimeoutMs > 0
          ? loadedConfig.idleTimeoutMs
          : undefined,
      licenseTypeLabelField,
      inspectionProcessTypeOverride: normalizedInspectionProcessTypeOverride,
      licenseNoteRequiredWhenMobileAppFalse,
      fisherCountryRequired: loadedConfig.fisherCountryRequired !== false,
      fisherBulkEditHidden: loadedConfig.fisherBulkEditHidden === true,
      fisherWorkAssignmentsHidden: loadedConfig.fisherWorkAssignmentsHidden === true,
      licenseViewFisherPhoneHidden: loadedConfig.licenseViewFisherPhoneHidden === true,
      licenseViewLanguageHidden: loadedConfig.licenseViewLanguageHidden === true,
      licenseViewBillingReferenceHidden: loadedConfig.licenseViewBillingReferenceHidden === true,
      licensesNotPrintedFilterHidden: loadedConfig.licensesNotPrintedFilterHidden === true,
      licensesNotFormalVerifiedFilterHidden: loadedConfig.licensesNotFormalVerifiedFilterHidden === true,
      licensesCancelledPrintEnabled: loadedConfig.licensesCancelledPrintEnabled === true,
      hunterCountryRequired: loadedConfig.hunterCountryRequired !== false,
      hunterBulkEditHidden: loadedConfig.hunterBulkEditHidden === true,
      hunterWorkAssignmentsHidden: loadedConfig.hunterWorkAssignmentsHidden === true,
      huntingLicensesNotPrintedFilterHidden: loadedConfig.huntingLicensesNotPrintedFilterHidden === true,
      huntingLicensesNotFormalVerifiedFilterHidden: loadedConfig.huntingLicensesNotFormalVerifiedFilterHidden === true,
      enableSalesPoint: loadedConfig.enableSalesPoint === true,
      sanaFormat: typeof loadedConfig.sanaFormat === 'string' ? loadedConfig.sanaFormat : undefined,
      version: typeof loadedConfig.version === 'string' ? loadedConfig.version : undefined,
      autoValidateLicense: loadedConfig.autoValidateLicense !== false,
      sentryEnabled: loadedConfig.sentryEnabled === true,
      sentryDsn:
        typeof loadedConfig.sentryDsn === 'string' && loadedConfig.sentryDsn ? loadedConfig.sentryDsn : undefined,
      sentryEnvironment:
        typeof loadedConfig.sentryEnvironment === 'string' && loadedConfig.sentryEnvironment
          ? loadedConfig.sentryEnvironment
          : undefined,
      sentryReplayOnErrorSampleRate:
        typeof loadedConfig.sentryReplayOnErrorSampleRate === 'number'
          ? loadedConfig.sentryReplayOnErrorSampleRate
          : undefined,
    };

    console.log('[ConfigService] Loaded config:', {
      apiBaseUrl: config.apiBaseUrl,
      rawApiBaseUrl: loadedConfig.apiBaseUrl,
      inspectionProcessTypeOverride: config.inspectionProcessTypeOverride,
    });
  } catch (error) {
    console.warn('Failed to load runtime config, using env var fallback');
  }
};

export const getConfig = () => {
  if (!config) {
    console.error('Config not loaded! Make sure config.json exists and loadConfig() was called.');
    return {
      apiBaseUrl: '',
      ssoLoginPath: DEFAULT_SSO_LOGIN_PATH,
      ssoLogoutPath: DEFAULT_SSO_LOGOUT_PATH,
      ssoCallbackPath: DEFAULT_SSO_CALLBACK_PATH,
      idleTimeoutMs: undefined,
      licenseTypeLabelField: DEFAULT_LICENSE_TYPE_LABEL_FIELD,
      inspectionProcessTypeOverride: undefined,
      licenseNoteRequiredWhenMobileAppFalse: false,
      fisherCountryRequired: true,
      fisherBulkEditHidden: false,
      fisherWorkAssignmentsHidden: false,
      hunterCountryRequired: true,
      hunterBulkEditHidden: false,
      hunterWorkAssignmentsHidden: false,
      licenseViewFisherPhoneHidden: false,
      licenseViewBillingReferenceHidden: false,
      licenseViewLanguageHidden: false,
      licensesNotPrintedFilterHidden: false,
      licensesNotFormalVerifiedFilterHidden: false,
      licensesCancelledPrintEnabled: false,
      huntingLicensesNotPrintedFilterHidden: false,
      huntingLicensesNotFormalVerifiedFilterHidden: false,
      enableSalesPoint: false,
      sanaFormat: undefined,
      version: undefined,
      autoValidateLicense: true,
      sentryEnabled: false,
      sentryDsn: undefined,
      sentryEnvironment: undefined,
      sentryReplayOnErrorSampleRate: undefined,
    };
  }
  return config;
};
